import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Coins, Loader2, RefreshCw, Search, ChevronLeft, ChevronRight, Users, UserPlus, Trash2, X, AlertTriangle, Settings2 } from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// ADMIN - Crédits de tous les clients, par module (lecture seule)
// Disposition : en-tête FIXE (titre + statistiques + filtres), liste qui DÉFILE
// (en-tête de colonnes figé), pagination en bas.
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

const fmt = (n) => (n || 0).toLocaleString('fr-FR');
const PAGE_SIZES = [10, 25, 50, 100];

// Chaque module : libellé, valeur numérique (tri / filtre / total), affichage, détail au survol
const MODULES = [
  { key: 'ia_video', label: 'IA Studio Vidéo', unit: 'Ar',
    value: c => c.ia_video.balance, show: c => `${fmt(c.ia_video.balance)} Ar`,
    hint: c => `Acheté ${fmt(c.ia_video.bought)} Ar · Utilisé ${fmt(c.ia_video.used)} Ar (même solde pour TeckiA Pub et le Réalisateur IA)` },
  { key: 'ia_image', label: 'IA Studio Image', unit: 'Ar',
    value: c => c.ia_image.balance, show: c => `${fmt(c.ia_image.balance)} Ar`,
    hint: c => `Acheté ${fmt(c.ia_image.bought)} Ar · Utilisé ${fmt(c.ia_image.used)} Ar` },
  { key: 'video_ads', label: 'Pub Vidéos', unit: 'vidéos',
    value: c => c.video_ads.remaining, show: c => `${fmt(c.video_ads.remaining)} vid.`,
    hint: c => `${fmt(c.video_ads.used)} utilisées sur ${fmt(c.video_ads.max)}` },
  { key: 'marketing', label: 'Marketing Studio', unit: '',
    value: c => c.marketing.images_remaining + c.marketing.videos_remaining,
    show: c => `${fmt(c.marketing.images_remaining)} img · ${fmt(c.marketing.videos_remaining)} vid.`,
    hint: () => 'Images et vidéos restantes' },
  { key: 'vtryon', label: 'Essayage virtuel', unit: '',
    value: c => c.vtryon.images + c.vtryon.videos,
    show: c => `${fmt(c.vtryon.images)} img · ${fmt(c.vtryon.videos)} vid.`,
    hint: () => 'Pool utilisateur (avant attribution aux pages)' },
  { key: 'cm', label: 'CM Studio', unit: 'Ar',
    value: c => c.cm.balance, show: c => `${fmt(c.cm.balance)} Ar`,
    hint: c => c.cm.pages.map(p => `${p.page} : ${fmt(p.balance)} Ar`).join('\n') || 'Aucune page avec solde' },
  { key: 'cv', label: 'Recrutement (CV)', unit: '',
    value: c => c.cv.balance, show: c => fmt(c.cv.balance),
    hint: () => 'Crédits CV' },
  { key: 'motion', label: 'Motion Vid', unit: 'Ar',
    value: c => c.motion.balance + (c.motion.sub_until ? 1 : 0),
    show: c => [c.motion.balance ? `${fmt(c.motion.balance)} Ar` : '', c.motion.sub_until ? `abo. → ${c.motion.sub_until}` : ''].filter(Boolean).join(' · '),
    hint: c => `Acheté ${fmt(c.motion.bought)} Ar · Utilisé ${fmt(c.motion.used)} Ar${c.motion.sub_until ? ` · Abonnement jusqu'au ${c.motion.sub_until}` : ''}` },
  { key: 'utile', label: 'Utile (chansons)', unit: 'Ar',
    value: c => c.utile.balance, show: c => `${fmt(c.utile.balance)} Ar`,
    hint: c => `Acheté ${fmt(c.utile.bought)} Ar · Utilisé ${fmt(c.utile.used)} Ar` },
  { key: 'voice', label: 'Voice Kar', unit: '',
    value: c => (c.voice.unlimited ? 1 : 0), show: c => `Illimité → ${c.voice.until}`,
    hint: () => 'Abonnement illimité en cours (sinon 10 séparations gratuites par mois)' },
  { key: 'agents', label: 'Agents IA', unit: '',
    value: c => c.agents.count, show: c => `${c.agents.count} actif${c.agents.count > 1 ? 's' : ''}`,
    hint: c => c.agents.slots.map(s => `${s.channel} · ${s.kind} → ${s.until}`).join('\n') },
];

const SEVEN_DAYS = 7 * 24 * 3600 * 1000;

// Retrait de crédits (Superadmin seulement) : ce qu'on peut retirer dans la cellule d'un module.
// wallet = on peut retirer un montant OU tout supprimer ; sinon seulement « tout supprimer / remettre à zéro ».
const actionsFor = (modKey, c) => {
  const wallet = (module, label, max, unit, extra = {}) => ({ module, label, max, unit, wallet: true, ...extra });
  switch (modKey) {
    case 'ia_video': case 'ia_image': case 'utile':
      return c[modKey].balance > 0 ? [wallet(modKey, 'Solde', c[modKey].balance, 'Ar')] : [];
    case 'cv':
      return c.cv.balance > 0 ? [wallet('cv', 'Crédits CV', c.cv.balance, 'crédits')] : [];
    case 'motion':
      return [
        ...(c.motion.balance > 0 ? [wallet('motion', 'Solde Motion Vid', c.motion.balance, 'Ar')] : []),
        ...(c.motion.sub_until ? [{ module: 'motion_sub', label: `Abonnement illimité (jusqu'au ${c.motion.sub_until})`, wallet: false }] : []),
      ];
    case 'cm': {
      const pages = c.cm.pages || [];
      return [
        ...pages.map(p => wallet('cm', `Page « ${p.page} »`, p.balance, 'Ar', { client_id: p.id })),
        ...(pages.length > 1 ? [{ module: 'cm', label: 'Toutes les Pages', wallet: false, client_id: '' }] : []),
      ];
    }
    case 'video_ads': return c.video_ads.remaining > 0 ? [{ module: 'video_ads', label: `Remettre à zéro (${c.video_ads.remaining} vidéos restantes)`, wallet: false }] : [];
    case 'marketing': return (c.marketing.images_remaining + c.marketing.videos_remaining) > 0 ? [{ module: 'marketing', label: 'Remettre à zéro (images et vidéos restantes)', wallet: false }] : [];
    case 'vtryon': return (c.vtryon.images + c.vtryon.videos) > 0 ? [{ module: 'vtryon', label: "Remettre à zéro (réserve d'essayage)", wallet: false }] : [];
    default: return [];
  }
};

// Fiche d'un client (clic sur sa ligne) : tous ses modules avec du crédit et, pour chacun, le bouton Retirer / Transférer
function ManageClientModal({ user, onClose, onPick }) {
  const rows = MODULES.map(m => ({ m, acts: actionsFor(m.key, user.credits) })).filter(r => r.acts.length > 0);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-slate-900 border border-white/15 rounded-2xl p-5 space-y-3 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2"><Settings2 className="w-5 h-5 text-yellow-400" /> Gérer les crédits</h3>
            <p className="text-xs text-gray-400 mt-0.5">{user.name || '(sans nom)'} · {user.email && !user.email.endsWith('@phone.teckia.local') ? user.email : user.phone}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10 text-gray-300"><X className="w-4 h-4" /></button>
        </div>
        {rows.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">Ce client n'a aucun crédit à retirer ou transférer.</p>}
        {rows.map(({ m, acts }) => (
          <div key={m.key} className="flex items-center justify-between gap-3 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
            <div className="min-w-0">
              <div className="text-gray-300 text-xs">{m.label}</div>
              <div className="text-green-300 font-semibold text-sm truncate">{m.show(user.credits)}</div>
            </div>
            <button onClick={() => onPick({ user, mod: m, actions: acts })}
              className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-400/40 text-red-100 text-xs font-semibold">
              {acts.some(a => a.wallet && ['ia_video', 'ia_image', 'utile', 'motion', 'cm'].includes(a.module)) ? 'Retirer / transférer' : 'Retirer'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const AR_MODULES = ['ia_video', 'ia_image', 'utile', 'motion', 'cm'];
const AR_LABEL = { ia_video: 'IA Studio Vidéo', ia_image: 'IA Studio Image', utile: 'Utile (chansons)', motion: 'Motion Vid' };

function RemoveCreditsModal({ target, onClose, onDone }) {
  const { user, mod, actions } = target;
  const [idx, setIdx] = useState(0);
  const [mode, setMode] = useState(actions[0].wallet ? 'remove' : 'reset');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const act = actions[idx];
  const effMode = act.wallet ? mode : 'reset';
  const canTransfer = act.wallet && AR_MODULES.includes(act.module);
  // destinations : les autres soldes en Ariary du client (une entrée par Page pour CM Studio)
  const dests = canTransfer ? [
    ...['ia_video', 'ia_image', 'utile', 'motion'].filter(k => k !== act.module).map(k => ({ v: k, module: k, client_id: '', label: AR_LABEL[k] })),
    ...(user.credits.cm.all_pages || []).filter(p => !(act.module === 'cm' && p.id === act.client_id)).map(p => ({ v: `cm:${p.id}`, module: 'cm', client_id: p.id, label: `CM Studio — ${p.page}` })),
  ] : [];
  const [dest, setDest] = useState('');
  const destObj = dests.find(x => x.v === dest) || dests[0];

  const submit = async () => {
    setErr('');
    if (reason.trim().length < 3) { setErr('Indiquez le motif du retrait.'); return; }
    if ((effMode === 'remove' || effMode === 'transfer') && !(Number(amount) > 0)) { setErr(effMode === 'transfer' ? 'Entrez le montant à transférer.' : 'Entrez le montant à retirer.'); return; }
    if (effMode === 'transfer' && !destObj) { setErr('Choisissez la destination.'); return; }
    setBusy(true);
    try {
      const isTr = effMode === 'transfer';
      const res = await fetch(`${API_URL}/api/admin/clients-credits/${isTr ? 'transfer' : 'adjust'}`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify(isTr
          ? { user_id: user.user_id, from: act.module, from_client_id: act.client_id || '', to: destObj.module, to_client_id: destObj.client_id, amount: Number(amount) || 0, reason: reason.trim() }
          : { user_id: user.user_id, module: act.module, mode: effMode, amount: Number(amount) || 0, reason: reason.trim(), client_id: act.client_id || '' }),
      });
      const d = await res.json();
      if (d.success) onDone(); else setErr(d.message || 'Retrait impossible.');
    } catch { setErr("Erreur réseau : rien n'a été confirmé, actualisez avant de réessayer."); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-slate-900 border border-red-500/40 rounded-2xl p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-400" /> Retirer ou transférer des crédits</h3>
            <p className="text-xs text-gray-400 mt-0.5">{user.name || '(sans nom)'} · {user.email && !user.email.endsWith('@phone.teckia.local') ? user.email : user.phone} · {mod.label}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10 text-gray-300"><X className="w-4 h-4" /></button>
        </div>

        {actions.length > 1 && (
          <div className="space-y-1">
            {actions.map((a, i) => (
              <label key={i} className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
                <input type="radio" checked={idx === i} onChange={() => { setIdx(i); setMode(a.wallet ? 'remove' : 'reset'); setAmount(''); }} />
                {a.label}{a.wallet ? ` — ${fmt(a.max)} ${a.unit}` : ''}
              </label>
            ))}
          </div>
        )}
        {actions.length === 1 && <p className="text-sm text-gray-200">{act.label}{act.wallet ? ` — ${fmt(act.max)} ${act.unit} au total` : ''}</p>}

        {act.wallet && (
          <div className="flex gap-2">
            {[{ v: 'remove', l: 'Retirer un montant' }, { v: 'reset', l: 'Tout supprimer' }, ...(canTransfer ? [{ v: 'transfer', l: 'Transférer' }] : [])].map(o => (
              <button key={o.v} onClick={() => setMode(o.v)}
                className={`flex-1 px-3 py-2 rounded-xl text-xs font-semibold border ${mode === o.v ? 'bg-red-500/20 border-red-400 text-red-200' : 'bg-white/5 border-white/10 text-gray-300'}`}>{o.l}</button>
            ))}
          </div>
        )}
        {act.wallet && mode === 'transfer' && (
          <select value={destObj ? destObj.v : ''} onChange={e => setDest(e.target.value)} className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-white/20 text-white text-sm">
            {dests.map(o => <option key={o.v} value={o.v}>Vers : {o.label}</option>)}
          </select>
        )}
        {act.wallet && (mode === 'remove' || mode === 'transfer') && (
          <input type="number" min="1" max={act.max} value={amount} onChange={e => setAmount(e.target.value)} placeholder={`Montant à ${mode === 'transfer' ? 'transférer' : 'retirer'} (max ${fmt(act.max)} ${act.unit})`}
            className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm placeholder-gray-400" />
        )}
        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Motif (obligatoire, gardé dans le journal)"
          className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm placeholder-gray-400" />
        <p className="text-[11px] text-red-300">{effMode === 'transfer'
          ? 'Le montant (1 Ar = 1 Ar) quitte ce module et arrive tout de suite dans la destination. Inscrit dans l’historique des deux portefeuilles et dans le journal serveur.'
          : 'Action irréversible : le client perd ces crédits tout de suite. Le retrait est inscrit dans son historique et dans le journal serveur.'}</p>
        {err && <p className="text-red-300 text-sm">{err}</p>}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-white/10 text-gray-200 text-sm">Annuler</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {effMode === 'reset' ? 'Supprimer' : effMode === 'transfer' ? 'Transférer' : 'Retirer'}
          </button>
        </div>
      </div>
    </div>
  );
}

const fmtDate = (iso) => {
  if (!iso) return { date: '—', ago: '' };
  const d = new Date(iso);
  if (isNaN(d)) return { date: '—', ago: '' };
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  const ago = days <= 0 ? "aujourd'hui" : days === 1 ? 'hier' : days < 30 ? `il y a ${days} j` : days < 365 ? `il y a ${Math.floor(days / 30)} mois` : `il y a ${Math.floor(days / 365)} an${days >= 730 ? 's' : ''}`;
  return { date: d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }), ago };
};

export default function ClientsCredits() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [onlyWithCredits, setOnlyWithCredits] = useState(false);
  const [moduleFilter, setModuleFilter] = useState('all');
  const [sortKey, setSortKey] = useState('created');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [isSuper, setIsSuper] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [managing, setManaging] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/clients-credits`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) setUsers(d.users); else setError(d.message || 'Chargement impossible.');
    } catch { setError('Erreur réseau.'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Le retrait de crédits n'est proposé qu'au Superadmin (le serveur le vérifie aussi : bouton caché ≠ droit)
  useEffect(() => {
    fetch(`${API_URL}/api/admin/staff/me`, { headers: authHeaders() }).then(r => r.json())
      .then(d => setIsSuper(!!(d.success && d.is_super && String(d.email || '').toLowerCase() === 'contact@tdidigital.io')))
      .catch(() => setIsSuper(false));
  }, []);

  // Retour à la page 1 dès qu'un filtre / tri / taille de page change
  useEffect(() => { setPage(1); }, [query, onlyWithCredits, moduleFilter, sortKey, pageSize]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = users.filter(u => !q || `${u.name} ${u.email} ${u.phone}`.toLowerCase().includes(q));
    if (moduleFilter !== 'all') {
      const m = MODULES.find(x => x.key === moduleFilter);
      list = list.filter(u => m.value(u.credits) > 0);
    } else if (onlyWithCredits) {
      list = list.filter(u => u.has_any);
    }
    if (sortKey === 'created_asc') {
      list = [...list].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
    } else if (sortKey !== 'created') {
      const m = MODULES.find(x => x.key === sortKey);
      list = [...list].sort((a, b) => m.value(b.credits) - m.value(a.credits));
    }
    return list;
  }, [users, query, onlyWithCredits, moduleFilter, sortKey]);

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Statistiques globales (tous les clients, indépendantes des filtres)
  const stats = useMemo(() => {
    const totals = {};
    MODULES.forEach(m => { totals[m.key] = users.reduce((sum, u) => sum + m.value(u.credits), 0); });
    const now = Date.now();
    return {
      totals,
      clients: users.length,
      withCredits: users.filter(u => u.has_any).length,
      recent: users.filter(u => u.created_at && now - new Date(u.created_at).getTime() < SEVEN_DAYS).length,
    };
  }, [users]);

  const statCards = [
    { label: 'Clients', value: fmt(stats.clients), sub: `${fmt(stats.withCredits)} avec crédits`, icon: Users },
    { label: 'Nouveaux (7 j)', value: fmt(stats.recent), sub: 'inscriptions', icon: UserPlus },
    ...MODULES.map(m => ({
      label: m.label,
      value: ['marketing', 'vtryon', 'motion', 'voice', 'agents'].includes(m.key)
        ? (m.key === 'motion' ? `${fmt(users.reduce((s, u) => s + u.credits.motion.balance, 0))} Ar` : fmt(stats.totals[m.key]))
        : `${fmt(stats.totals[m.key])}${m.unit ? ' ' + m.unit : ''}`,
      sub: `${fmt(users.filter(u => m.value(u.credits) > 0).length)} clients`,
    })),
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] lg:h-screen p-3 md:p-5 gap-3 box-border">
      {/* ── PARTIE FIXE : titre, statistiques, filtres ── */}
      <div className="flex-shrink-0 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2.5">
              <Coins className="w-7 h-7 text-yellow-400" /> Crédits des clients
            </h1>
            <p className="text-xs text-blue-200">Tous les clients et leurs crédits restants, module par module. {isSuper ? 'Superadmin : cliquez sur un client (ou « Gérer ») pour retirer ou transférer ses crédits.' : 'Lecture seule.'}</p>
          </div>
          <button onClick={load} className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white" title="Actualiser">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-2">
          {statCards.map(c => (
            <div key={c.label} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2">
              <div className="text-gray-400 text-[10px] flex items-center gap-1 truncate">
                {c.icon && <c.icon className="w-3 h-3 flex-shrink-0" />} {c.label}
              </div>
              <div className="text-white font-bold text-base leading-tight truncate">{c.value}</div>
              <div className="text-gray-500 text-[10px] truncate">{c.sub}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Rechercher un client (nom, email, téléphone)…"
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm placeholder-gray-400" />
          </div>
          <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-800 border border-white/20 text-white text-sm">
            <option value="all">Tous les modules</option>
            {MODULES.map(m => <option key={m.key} value={m.key}>Avec crédit : {m.label}</option>)}
          </select>
          <select value={sortKey} onChange={e => setSortKey(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-800 border border-white/20 text-white text-sm">
            <option value="created">Tri : plus récents</option>
            <option value="created_asc">Tri : plus anciens</option>
            {MODULES.map(m => <option key={m.key} value={m.key}>Tri : {m.label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-gray-200 text-sm px-2 cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={onlyWithCredits} onChange={e => setOnlyWithCredits(e.target.checked)} />
            Seulement avec crédits
          </label>
        </div>
        {error && <p className="text-red-300 text-sm">{error}</p>}
      </div>

      {/* ── LISTE QUI DÉFILE ── */}
      {loading && users.length === 0 ? (
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-10 h-10 text-blue-400 animate-spin" /></div>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto bg-white/5 border border-white/10 rounded-2xl">
          <table className="w-full text-sm min-w-[1500px]">
            <thead className="sticky top-0 z-10 bg-slate-900">
              <tr className="text-left text-gray-400 text-xs border-b border-white/10">
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-3 py-3 font-medium whitespace-nowrap">Inscrit le</th>
                {MODULES.map(m => <th key={m.key} className="px-3 py-3 font-medium whitespace-nowrap">{m.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {pageRows.map(u => (
                <tr key={u.user_id} className={`border-b border-white/5 hover:bg-white/5 ${isSuper ? 'cursor-pointer' : ''}`} onClick={isSuper ? () => setManaging(u) : undefined}>
                  <td className="px-4 py-2.5">
                    <div className="text-white font-medium flex items-center gap-2">
                      {u.name || '(sans nom)'}
                      {isSuper && (
                        <button onClick={e => { e.stopPropagation(); setManaging(u); }} title="Retirer ou transférer des crédits"
                          className="px-2 py-0.5 rounded-md bg-yellow-500/15 hover:bg-yellow-500/25 border border-yellow-400/30 text-yellow-200 text-[10px] font-semibold flex items-center gap-1">
                          <Settings2 className="w-3 h-3" /> Gérer
                        </button>
                      )}
                    </div>
                    <div className="text-gray-400 text-xs">{u.email && !u.email.endsWith('@phone.teckia.local') ? u.email : u.phone}</div>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="text-gray-200 text-xs">{fmtDate(u.created_at).date}</div>
                    <div className="text-gray-500 text-[10px]">{fmtDate(u.created_at).ago}</div>
                  </td>
                  {MODULES.map(m => {
                    const has = m.value(u.credits) > 0;
                    const acts = isSuper && has ? actionsFor(m.key, u.credits) : [];
                    return (
                      <td key={m.key} className="px-3 py-2.5 whitespace-nowrap" title={m.hint(u.credits)}>
                        <span className={has ? 'text-green-300 font-medium' : 'text-gray-600'}>{has ? m.show(u.credits) : '—'}</span>
                        {acts.length > 0 && (
                          <button onClick={e => { e.stopPropagation(); setRemoving({ user: u, mod: m, actions: acts }); }} title="Retirer ou transférer des crédits (Superadmin)"
                            className="ml-2 p-1 rounded-md text-red-300/70 hover:text-red-200 hover:bg-red-500/20 align-middle"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={MODULES.length + 2} className="px-4 py-8 text-center text-gray-500">Aucun client trouvé.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {managing && !removing && <ManageClientModal user={managing} onClose={() => setManaging(null)} onPick={t => { setRemoving(t); }} />}
      {removing && <RemoveCreditsModal target={removing} onClose={() => setRemoving(null)} onDone={() => { setRemoving(null); setManaging(null); load(); }} />}

      {/* ── PAGINATION FIXE EN BAS ── */}
      <div className="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-gray-300">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Par page</span>
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
            className="px-2 py-1 rounded-lg bg-slate-800 border border-white/20 text-white text-xs">
            {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span className="text-xs text-gray-400">
            {rows.length === 0 ? '0 client' : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, rows.length)} sur ${rows.length} client${rows.length > 1 ? 's' : ''}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-xs">Page {safePage} / {pageCount}</span>
          <button onClick={() => setPage(p => Math.min(pageCount, p + 1))} disabled={safePage >= pageCount}
            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}
